/* THE EMAIL'S WAY OUT (her call, 2026-10-12): Plain | Designed, then Copy.
   Shared by every Momentum record that is an email — a Newsletter email, an
   Outreach touch on the email channel — and knows nothing about either: it is
   handed the parts (`utils/emailHtml.js`) and a brand. Nothing here SENDS an
   email: "Send to GHL" (admin) files it as a TEMPLATE in her email builder.

   Designed copies FORMATTED (paste into Gmail and it arrives laid out), with
   "Copy the code" under it for the tools that take code (Mailchimp, Kit). The
   subject has its own Copy: it goes in the subject field, never the body. */

import { useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { B, btnGhost } from "./tokens";
import { useCrmConnected } from "./mediaPicker";
import { Choice } from "./ui";
import { copyFormatted, copyText, emailHtml, emailPlain, GHL_FIRST_NAME, personaliseEmail, readEmailFormat, writeEmailFormat } from "../../utils/emailHtml";
import { campaignSteps, sendEmailTemplate } from "../../utils/ghlHandoff";

const SAID = {
  subject: "Subject copied — paste it into the subject line.",
  plain: "Copied — the words, ready to paste anywhere.",
  designed: "Copied, designed — paste it into Gmail or Outlook and it keeps the layout. For Mailchimp or Kit, use Copy the code.",
  designedPlainOnly: "This browser only copies words, so it copied the words. Copy the code still gives you the design.",
  code: "Code copied — paste it where Mailchimp, Kit or GHL asks for HTML.",
};

export function EmailCopy({ parts, brand = {}, picture = null, where = "newsletter", defaultFormat = "designed", ghlName = "", handoff = null }) {
  const [format, setFormatState] = useState(() => readEmailFormat(where, defaultFormat));
  const [said, setSaid] = useState(null);       // { ok, text }
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);       // the template GHL now holds, for the campaign steps
  const { session } = useSession();
  /* SEND TO GHL (2026-10-12): admin only for now, like every GHL button —
     the connection is hers. Shown only with a name to file it under. */
  const crm = useCrmConnected();
  const setFormat = (v) => { setFormatState(v); writeEmailFormat(where, v); setSaid(null); };

  if (!emailPlain(parts).trim()) return null;
  /* HI AND TALK SOON (her ask, 2026-11-02). Copied, the reader's name is
     GHL's token only where she has GHL; anyone else gets a visible
     [first name] for their own tool's. Send to GHL fills its own. */
  const ready = personaliseEmail(parts, { to: crm ? GHL_FIRST_NAME : "", signer: brand.signer });
  const plain = emailPlain(ready);
  const html = () => emailHtml(ready, { brand, picture });

  const report = (r, okText) => setSaid(r.ok ? { ok: true, text: okText } : { ok: false, text: `It didn't copy: ${r.error}` });
  const copyEmail = async () => {
    if (format === "plain") return report(await copyText(plain), SAID.plain);
    const r = await copyFormatted(html(), plain);
    report(r, r.formatted ? SAID.designed : SAID.designedPlainOnly);
  };

  /* The SAME strings Copy uses go to GHL, so what lands there is what she
     would have pasted. A template, never a send; a second press updates it. */
  const sendToGhl = async () => {
    if (sending) return;
    setSending(true);
    setSaid(null);
    const r = await sendEmailTemplate({ token: await session?.getToken().catch(() => null), name: ghlName, format, parts, brand, picture });
    setSending(false);
    setSent(r.ok ? r : null);
    setSaid(r.ok
      ? { ok: true, text: `${r.updated ? "Updated" : "Saved"} in GHL as the ${format} template “${r.name}”.${handoff === "campaign" ? "" : " GHL keeps the subject on the campaign or workflow step, so use Copy subject there."}` }
      : { ok: false, text: `It didn't reach GHL: ${r.error}` });
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div role="radiogroup" aria-label="Email format" style={{ display: "inline-flex", gap: 6 }}>
          <Choice on={format === "plain"} onClick={() => setFormat("plain")}>Plain</Choice>
          <Choice on={format === "designed"} onClick={() => setFormat("designed")}>Designed</Choice>
        </div>
        {parts.subject && (
          <button onClick={async () => report(await copyText(parts.subject), SAID.subject)} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>
            Copy subject
          </button>
        )}
        <button onClick={copyEmail} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>
          Copy email
        </button>
        {crm && ghlName && (
          <button onClick={sendToGhl} disabled={sending} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5, opacity: sending ? 0.6 : 1 }}>
            {sending ? "Sending…" : "Send to GHL"}
          </button>
        )}
        {format === "designed" && (
          <button onClick={async () => report(await copyText(html()), SAID.code)}
            style={{ border: "none", background: "none", padding: "4px 2px", cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: B.teal, textDecoration: "underline" }}>
            Copy the code
          </button>
        )}
      </div>
      {/* NEWSLETTER → CAMPAIGN (her ask, 2026-10-12). GHL's API cannot make
          a campaign, so once the template is there the rest is three clicks
          she does — named here, with the subject a press away. */}
      {handoff === "campaign" && sent && (
        <ol aria-label="Make the campaign in GHL" style={{ margin: "10px 0 0", paddingLeft: 20, fontSize: 12.5, color: B.text, lineHeight: 1.7 }}>
          {campaignSteps({ name: ghlName, subject: parts.subject, preview: parts.preview }).map((step) => (
            <li key={step}>
              {step}
              {step.startsWith("Subject: ") && parts.subject && (
                <button onClick={async () => report(await copyText(parts.subject), SAID.subject)}
                  style={{ marginLeft: 8, border: "none", background: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: B.teal, textDecoration: "underline" }}>
                  Copy
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
      {said && (
        <div role="status" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5, color: said.ok ? B.dim : B.amber }}>{said.text}</div>
      )}
    </div>
  );
}
